import React from 'react'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Location } from '@/core/api/entities/location'
import { Zone } from '@/core/api/entities/zone/zone'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import PageLayout from '@/shared/components/organisms/Layout/PageLayout'
import { getCountryFlag } from '@/shared/utils/countryFlags'

interface LocationDetailsUIProps {
	location?: Location
	isLoading: boolean
	error: any
	onBack: () => void
	onChangeInformation: () => void
	canEditLocation?: boolean
	onShareAccess: () => void
	zones?: Zone[]
	onZoneClick?: (zone: Zone) => void
	openDeleteModal?: (zone: Zone) => void
	openRefreshModal?: (zone: Zone) => void
	openEditModal?: (zone: Zone) => void
	openSendCodeEmailModal?: (zone: Zone) => void
	handleAddZone?: () => void
	handleConnectZone?: (zone: Zone) => void
	handleUpdateZone?: (zoneId: number) => (data: UpdateZoneRequestDTO) => void
	isUpdating?: boolean
	isTrialAccount?: boolean
}

const LocationDetailsUI: React.FC<LocationDetailsUIProps> = ({
	location,
	isLoading,
	error,
	onBack,
	onChangeInformation,
	canEditLocation = false,
	onShareAccess,
	openDeleteModal: _openDeleteModal,
	openSendCodeEmailModal: _openSendCodeEmailModal,
}) => {
	const { t } = useTranslation('zones')
	const { features } = usePermission()

	if (isLoading) {
		return (
			<PageLayout
				backButton={{ show: true, onClick: onBack }}
				title={t('location.title')}
				contentClassName="min-h-[calc(100vh-300px)]"
			>
				<div className="space-y-6">
					<Skeleton className="h-6 w-48 md:h-8 md:w-64" />
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
						<div className="space-y-4 md:space-y-6">
							<Skeleton className="h-5 w-36 md:h-6 md:w-48" />
							<div className="space-y-3">
								<Skeleton className="h-3 w-16" />
								<Skeleton className="h-4 w-3/4" />
							</div>
							<div className="space-y-3">
								<Skeleton className="h-3 w-16" />
								<Skeleton className="h-4 w-full" />
							</div>
							<div className="space-y-3">
								<Skeleton className="h-3 w-16" />
								<Skeleton className="h-4 w-1/2" />
							</div>
						</div>
						<div className="space-y-4 md:space-y-6">
							<Skeleton className="h-5 w-36 md:h-6 md:w-48" />
							<div className="space-y-3">
								<Skeleton className="h-3 w-16" />
								<Skeleton className="h-4 w-3/4" />
							</div>
							<div className="space-y-3">
								<Skeleton className="h-3 w-16" />
								<Skeleton className="h-4 w-full" />
							</div>
							<div className="space-y-3">
								<Skeleton className="h-3 w-16" />
								<Skeleton className="h-4 w-1/2" />
							</div>
						</div>
					</div>
				</div>
			</PageLayout>
		)
	}

	if (error || !location) {
		return (
			<PageLayout
				backButton={{ show: true, onClick: onBack }}
				title={t('location.title')}
				contentClassName="px-8 py-12"
			>
				<div className="text-center">
					<h2 className="text-2xl font-semibold mb-4">
						{t('location.notFound')}
					</h2>
					<p className="text-muted-foreground mb-6">
						{t('location.notFoundDescription')}
					</p>
					<Button
						onClick={onBack}
						variant="default"
						className="rounded-full bg-white text-black hover:bg-gray-100"
					>
						{t('location.goBack')}
					</Button>
				</div>
			</PageLayout>
		)
	}

	return (
		<PageLayout
			backButton={{ show: true, onClick: onBack }}
			title={t('location.title')}
			contentClassName="px-8 py-6 min-h-[calc(100vh-300px)]"
		>
			{/* Location and Manager Information */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
				{/* Location Information */}
				<div className="space-y-6">
					<h2 className="text-xl font-semibold mb-6">
						{t('location.information.title')}
					</h2>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.information.name')}
						</label>
						<p className="text-base">{location.name}</p>
					</div>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.information.address')}
						</label>
						<p className="text-base">{location.address || '-'}</p>
					</div>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.information.country')}
						</label>
						<div className="flex items-center gap-2">
							{location.country && getCountryFlag(location.country)}
							<p className="text-base">
								{location.country
									? new Intl.DisplayNames(['en'], {
											type: 'region',
										}).of(location.country)
									: '-'}
							</p>
						</div>
					</div>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.information.city')}
						</label>
						<p className="text-base">{location.city || '-'}</p>
					</div>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.information.zipCode')}
						</label>
						<p className="text-base">{location.zipCode || '-'}</p>
					</div>
				</div>

				{/* Manager Information */}
				<div className="space-y-6">
					<h2 className="text-xl font-semibold mb-6">
						{t('location.manager.title')}
					</h2>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.manager.fullName')}
						</label>
						<p className="text-base">{location.manager || '-'}</p>
					</div>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.manager.email')}
						</label>
						<p className="text-base">{location.email || '-'}</p>
					</div>

					<div>
						<label className="text-sm text-muted-foreground block mb-2">
							{t('location.manager.phone')}
						</label>
						<p className="text-base">{location.phoneNumber || '-'}</p>
					</div>

					{location.additionalInformation && (
						<div>
							<label className="text-sm text-muted-foreground block mb-2">
								{t('location.manager.additionalInfo')}
							</label>
							<p className="text-base">{location.additionalInformation}</p>
						</div>
					)}
				</div>
			</div>

			{/* Change Information Button */}
			{canEditLocation && (
				<div className="mb-12">
					<Button
						variant="outline"
						onClick={onChangeInformation}
						className="rounded-full px-6"
					>
						{t('location.actions.changeInformation')}
					</Button>
				</div>
			)}

			<hr className="my-8 border-border" />

			{/* Share Location Access Section */}
			{features.location.share && (
				<div className="space-y-4">
					<h2 className="text-xl font-semibold">
						{t('location.actions.shareAccessTitle')}
					</h2>
					<p className="text-muted-foreground text-sm max-w-2xl">
						{t('location.actions.shareAccessDescription')}
					</p>
					<div>
						<Button
							variant="outline"
							onClick={onShareAccess}
							className="rounded-full px-6"
						>
							{t('location.actions.shareAccess')}
						</Button>
					</div>
				</div>
			)}
		</PageLayout>
	)
}

export default LocationDetailsUI
